import { BUILT_IN_PROFILES } from 'trace-viewer/model-utils';
import type { ModelIssue, ModelSummary, SummaryReason } from 'trace-viewer/model-utils';
import type { UiState } from './main';
import {
  axesToFixes,
  escapeHtml,
  renderDropZone,
  renderFileChip,
  renderFixSection,
  renderPageHeader,
  renderProgress,
  renderQualityPicker,
  renderSizeMeter,
  renderStatusHead,
  type FixItem,
  type QualityOption,
} from '../shared/ui';
import { renderAdjust } from './render-adjust';
import { CHECK_LABELS, renderDetails } from './render-details';
import { fixListHeading } from './result-state';

interface RenderProps {
  state: UiState;
  summary: ModelSummary | null;
  /** Settings changed since the current output was made. */
  dirty: boolean;
}

const ACCEPT = '.glb,.gltf,.glbx';

/** The shared quality words, mapped onto the model profiles. High → Small. */
const QUALITY: Record<string, Omit<QualityOption, 'id'>> = {
  'AR-high': { label: 'High', hint: 'Keeps fine detail. Best when viewers are on Wi-Fi.' },
  'AR-medium': { label: 'Balanced', hint: 'Recommended. Looks good and loads quickly on mobile data.' },
  'AR-low': { label: 'Small', hint: 'Smallest file, for slow connections or always-on background content.' },
};

const QUALITY_OPTIONS: QualityOption[] = BUILT_IN_PROFILES
  .filter(p => QUALITY[p.id])
  .map(p => ({ id: p.id, ...QUALITY[p.id] }));

const qualityLabel = (id: string): string => QUALITY[id]?.label ?? id;

/** The headline beside the light. One line per reason — no status guessing. */
const HEADLINE: Record<SummaryReason, string> = {
  unreadable: 'Couldn’t read this model',
  'blocking-issues': 'Needs fixing before AR',
  'over-budget': 'Works, but heavy for AR',
  'quality-warnings': 'Works — a few things to check',
  ready: 'Ready for AR',
};

// ─── Result panel ──────────────────────────────────────────────────────────

function renderResult(state: UiState, summary: ModelSummary | null, dirty: boolean): string {
  const o = state.optimization;
  const report = state.report;
  if (!report) return '';

  const optimized = o.report != null;
  const quality = qualityLabel(state.selectedProfile.id);
  const firstRun = o.running && !o.output;

  const head = firstRun
    ? renderStatusHead('busy', 'Optimizing…', 'Shrinking textures and simplifying the mesh. This runs in your browser.')
    : summary
      ? renderStatusHead(summary.status, HEADLINE[summary.reason],
          `${optimized ? 'Checked after optimizing' : 'Checked on the original file'}, against the <strong>${escapeHtml(quality)}</strong> target.`)
      : '';

  let actions = '';
  if (o.output && !o.running) {
    actions = dirty
      ? `<button class="btn-primary" data-action="optimize">Apply changes</button>
         <button data-action="download-output">Download previous</button>`
      : `<button class="btn-primary btn-large" data-action="download-output">Download GLB</button>`;
  } else if (!o.output && !o.running && !report.bailout) {
    actions = '<button class="btn-primary btn-large" data-action="optimize">Optimize</button>';
  }

  let body = '';
  if (o.running) {
    body = renderProgress(o.stage === 'Starting' ? 'Starting' : `${o.stage}…`, o.progress);
  } else if (o.output && state.file) {
    const note = dirty
      ? 'You’ve changed settings since this result. Apply them to update the preview and download.'
      : `Optimized at <strong>${escapeHtml(qualityLabel(state.selectedProfile.id))}</strong> quality.`;
    body = renderSizeMeter(state.file.size, state.file.size - o.bytesSaved, { note });
  }

  const bailout = report.bailout
    ? `<div class="notice error"><strong>This file can’t be optimized here.</strong> ${escapeHtml(report.bailout)} See what to fix below.</div>`
    : '';
  const error = o.error ? `<div class="notice error"><strong>Optimizing failed.</strong> ${escapeHtml(o.error)}</div>` : '';

  return `
    <div class="status-line">${head}${actions ? `<div class="status-actions">${actions}</div>` : ''}</div>
    ${body}
    ${bailout}
    ${error}
    ${report.bailout ? '' : renderQualityPicker(QUALITY_OPTIONS, state.selectedProfile.id)}
  `;
}

// ─── Fix list ──────────────────────────────────────────────────────────────

function issueToFix(issue: ModelIssue): FixItem | null {
  if (issue.severity === 'info') return null;
  return {
    status: issue.severity === 'error' ? 'red' : 'yellow',
    title: issue.title,
    detail: issue.detail,
    subject: issue.subject,
  };
}

function renderFixes(state: UiState, summary: ModelSummary | null, dirty: boolean): string {
  if (!summary || (state.optimization.running && !state.optimization.output)) return '';
  const issues = summary.highlights.map(issueToFix).filter((f): f is FixItem => f !== null);
  // The "could it be read" axis duplicates the bailout issue; skip it here.
  const budget = axesToFixes(summary.verdict.axes.filter(a => a.axis !== 'analysis'), CHECK_LABELS);
  const items = [...issues, ...budget].sort((a, b) => (a.status === b.status ? 0 : a.status === 'red' ? -1 : 1));
  const { title, lede } = fixListHeading({ optimized: state.optimization.report != null, dirty, count: items.length });
  return renderFixSection(title, items, 'Nothing to fix. This model is ready for AR.', lede);
}

// ─── Shell ─────────────────────────────────────────────────────────────────

/**
 * Build the stable page shell ONCE: slot <div>s that re-renders rewrite, plus
 * a `#preview-slot` holding the live <trace-viewer> elements. The preview slot
 * is never rewritten, so its WebGL contexts survive and the preview can sit
 * inside the result panel.
 */
function ensureShell(root: HTMLElement): void {
  if (root.querySelector('#preview-slot')) return;
  root.innerHTML = `
    ${renderPageHeader('model', 'Model optimizer', 'Shrink a GLB for AR and fix what would stop it loading or looking right.')}
    <div data-slot="input"></div>
    <div data-slot="notice"></div>
    <section class="result" hidden>
      <div class="result-body" data-slot="result"></div>
      <div id="preview-slot" class="preview-grid" hidden></div>
    </section>
    <div data-slot="fixes"></div>
    <div data-slot="adjust"></div>
    <div data-slot="details"></div>
    <div id="texture-modal" class="modal" role="dialog" aria-label="Texture preview">
      <img id="texture-modal-img" alt="" />
      <div id="texture-modal-caption" class="modal-caption"></div>
      <div class="modal-hint">Click anywhere or press Esc to close</div>
    </div>
  `;
}

export function renderApp(root: HTMLElement, props: RenderProps): void {
  const { state, summary, dirty } = props;
  ensureShell(root);

  const setSlot = (name: string, html: string): void => {
    const el = root.querySelector(`[data-slot="${name}"]`);
    if (el) el.innerHTML = html;
  };

  setSlot('input', state.file
    ? renderFileChip(state.file, ACCEPT)
    : renderDropZone({ accept: ACCEPT, prompt: 'Drop a 3D model here', hint: 'GLB or glTF.' }));

  setSlot('notice', state.phase === 'probing'
    ? '<div class="busy-line"><span class="status-light busy"></span>Reading the model…</div>'
    : state.error
      ? `<div class="notice error" style="margin-top:1rem;"><strong>Couldn’t read this model.</strong> ${escapeHtml(state.error)}</div>`
      : '');

  const result = root.querySelector<HTMLElement>('section.result');
  if (result) result.hidden = !state.report;
  setSlot('result', renderResult(state, summary, dirty));
  setSlot('fixes', renderFixes(state, summary, dirty));
  setSlot('adjust', renderAdjust(state, dirty));
  setSlot('details', state.report ? renderDetails(state.report, summary, state.optimization.changes) : '');
}
